import { Box, Flex, VStack } from "@chakra-ui/react";
import { ItemsList } from "@/layouts/ItemsList";
import { Periodicity } from "@/layouts/Periodicity";
import { PaymenthsMethods } from "@/layouts/PaymenthsMethods";
import { useCheckout, usePlan, useSettings } from "@/hooks";
import { Bullet, Plan, PlanWithAdditionalDetails } from "@/models/Plan";
import { useEffect, useState } from "react";
import { formatCurrencyToApi, handleApiError, showToast } from "@/utils";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { Divider, Drawer, Input } from "barte-design-system";

const createPlanPageSchema = yup.object().shape({
  title: yup
    .string()
    .required("Preenchimento obrigatório!")
    .max(60, "O título deve ter no máximo 60 caracteres!"),
  monthly: yup.string().required("Preenchimento obrigatório"),
  semester: yup.string().required("Preenchimento obrigatório"),
  yearly: yup.string().required("Preenchimento obrigatório"),
});

interface UsePlanFormParams {
  planId?: string;
  onSuccess: () => void;
}

function usePlanForm({ planId, onSuccess }: UsePlanFormParams) {
  const { getSellerCheckoutDefault } = useSettings();
  const { getCheckoutDefault } = useCheckout();
  const { createPlan, updatePlan, getPlan } = usePlan();
  const [basicValues, setBasicValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typedLetters, setTypedLetters] = useState(0);
  const [initialData, setInitialData] = useState<Plan>({} as Plan);

  const form = useForm({
    resolver: yupResolver(createPlanPageSchema),
  });
  const { setValue, handleSubmit, reset } = form;

  async function getPaymentMethodsDefault() {
    try {
      const response = await getCheckoutDefault();
      const { data } = await getSellerCheckoutDefault();

      setInitialData({
        bullets: [],
        enableBankSlip: data.defaultEnableBankSlip,
        enableCreditCard: data.defaultEnableCreditCard,
        enablePixPayment: data.defaultEnablePixPayment,
        idCheckout: response.data.id,
      } as Plan);
    } catch (error) {
      handleApiError(error, "Não foi possível carregar os dados do checkout.");
    }
  }

  async function getInitialData() {
    if (planId === undefined) return;
    reset({ title: "", description: "", bullets: [] });
    setBasicValues([]);
    setTypedLetters(0);
    setInitialData({} as Plan);
    if (planId === "0") {
      getPaymentMethodsDefault();
      return;
    }
    try {
      const { data } = await getPlan(Number(planId));
      setInitialData(data);
      setBasicValues(data?.basicValues);
      setTypedLetters(data.description?.length);
      setValue("description", data?.description);
      setValue("title", data?.title);
      setValue("bullets", data?.bullets);
      setValue("id", data?.id);
    } catch (error) {
      handleApiError(error, "Não foi possível carregar o plano.");
    }
  }

  function handlePlans(bullets: Bullet[]): void {
    setInitialData({
      ...initialData,
      bullets: bullets,
    });

    setValue("bullets", bullets);
  }

  const handleSavePlan: SubmitHandler<PlanWithAdditionalDetails> = async (
    values,
    event
  ) => {
    event.preventDefault();
    setLoading(true);

    const plan = {
      id: initialData?.id,
      title: values?.title,
      description: values?.description,
      bullets: values?.bullets,
      basicValues: [
        {
          type: "MONTHLY",
          value: formatCurrencyToApi(values?.monthly),
          valuePerMonth: formatCurrencyToApi(values?.monthly),
        },
        {
          type: "SEMESTER",
          value: formatCurrencyToApi(values?.semester) * 6,
          valuePerMonth: formatCurrencyToApi(values?.semester),
        },
        {
          type: "YEARLY",
          value: formatCurrencyToApi(values?.yearly) * 12,
          valuePerMonth: formatCurrencyToApi(values?.yearly),
        },
      ],
      idCheckout: initialData?.idCheckout,
      enableBankSlip: values?.enableBankSlip,
      enableCreditCard: values?.enableCreditCard,
      enablePixPayment: values?.enablePixPayment,
    };

    try {
      if (initialData.id !== 0 && initialData.id !== undefined) {
        await updatePlan(initialData.id, plan);
      } else {
        await createPlan(plan);
      }

      showToast({ message: "Plano atualizado com sucesso!", type: "success" });
      onSuccess();
    } catch (error) {
      handleApiError(error, "Não foi possível salvar o plano.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, [planId]);

  return {
    form,
    loading,
    initialData,
    basicValues,
    typedLetters,
    setTypedLetters,
    handlePlans,
    submit: handleSubmit(handleSavePlan),
  };
}

type PlanFormFieldsProps = ReturnType<typeof usePlanForm>;

function PlanFormFields({
  form,
  initialData,
  basicValues,
  typedLetters,
  setTypedLetters,
  handlePlans,
  submit,
}: PlanFormFieldsProps) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;

  return (
    <VStack as="form" spacing={6} align="stretch" w="100%" onSubmit={submit}>
      <Input
        type="text"
        label="Título"
        placeholder="Título"
        defaultValue={initialData?.title}
        error={errors?.title?.message as string}
        {...register("title")}
      />

      <Box w="100%">
        <Input
          type="textarea"
          label="Descrição"
          labelOptional="opcional"
          placeholder="Descrição"
          rows={4}
          maxLength={500}
          defaultValue={initialData?.description}
          onKeyUp={(e) =>
            setTypedLetters(
              (e.target as HTMLTextAreaElement)?.value.split("").length
            )
          }
          {...register("description")}
        />
        <Flex justify="flex-end" color="gray.300" fontSize="sm">
          {typedLetters}/500
        </Flex>
      </Box>

      <Divider />

      <Periodicity
        setValue={setValue}
        basicValues={basicValues}
        register={register}
        errors={errors}
        watch={watch}
      />

      <Divider />

      <PaymenthsMethods
        data={initialData}
        register={register}
        errors={errors?.paymentMethods}
      />

      <ItemsList
        tooltip="Produtos ou serviços inclusos no plano"
        optional
        label="Itens"
        name="itemsList"
        list={initialData?.bullets}
        updateList={handlePlans}
        buttonText="Salvar"
      />
    </VStack>
  );
}

interface PlanFormDrawerProps {
  isOpen: boolean;
  planId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function PlanFormDrawer({
  isOpen,
  planId,
  onClose,
  onSuccess,
}: PlanFormDrawerProps) {
  const planForm = usePlanForm({ planId, onSuccess });

  return (
    <Drawer
      isOpen={isOpen}
      title={planId === "0" ? "Novo plano" : "Editar plano"}
      onClose={onClose}
      footer={{
        primaryButton: {
          children: planForm.loading ? "Salvando..." : "Salvar",
          onClick: planForm.submit,
          loading: planForm.loading,
        },
        secondaryButton: {
          children: "Cancelar",
          onClick: onClose,
          variant: "outline",
        },
      }}
    >
      <PlanFormFields {...planForm} />
    </Drawer>
  );
}
